/**
 * lib/briefingCache.js — Phase 84.5
 *
 * Two responsibilities:
 *   1. `invalidateBriefing()` — fire-and-forget POST to the backend to
 *      delete the current-week Financial Briefing cache row. Called
 *      whenever the user mutates a signal the briefing depends on
 *      (today: recurring commitments; extendable to transaction category
 *      edits, goal changes, etc.).
 *   2. Module-level `readSnapshot()` / `writeSnapshot()` — a lightweight
 *      in-memory cache of the last successful useDashboardData() state,
 *      so navigating away from and back to /dashboard renders the last
 *      known-good view instantly instead of showing skeleton loaders on
 *      every re-mount. The background refresh still runs; the user just
 *      doesn't stare at gray boxes while it does.
 *
 * Why not sessionStorage? The dashboard payload is chunky (~1-3 MB with
 * a 6-month transaction slice) and only useful for the current tab.
 * A module variable is stripped on tab close, cheaper to read/write,
 * and never blocks the UI thread on JSON.parse.
 */
import { supabase } from '@/lib/supabaseClient';

const API = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Fire-and-forget cache invalidation
// ---------------------------------------------------------------------------

/**
 * Delete the current-week briefing cache row for this user.
 *
 * Fire-and-forget: never awaited by the caller, never throws. If the
 * backend call fails we swallow the error — the worst case is that the
 * user sees stale copy for a few more minutes.
 *
 * Also clears the in-memory dashboard snapshot so the next dashboard
 * mount forces a fresh network fetch (otherwise the stale snapshot
 * would win the race against the new backend recompose).
 */
export function invalidateBriefing() {
  clearSnapshot();
  (async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token || !API) return;
      // We deliberately don't await this — the UI has already updated
      // optimistically and the user shouldn't wait on a background
      // cache-eviction call.
      await fetch(`${API}/api/dashboard/briefing/invalidate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        keepalive: true,
      });
    } catch { /* swallow — see docblock */ }
  })();
}

// ---------------------------------------------------------------------------
// Module-level dashboard snapshot cache (stale-while-revalidate)
// ---------------------------------------------------------------------------

let _snapshot = null; // { uid, state, ts }

/**
 * Read the last cached dashboard state for the given user id.
 * Returns null if the cache is empty, belongs to a different user, or
 * is older than the max age (default: 15 minutes).
 */
export function readSnapshot(uid, { maxAgeMs = 15 * 60 * 1000 } = {}) {
  if (!_snapshot || !uid || _snapshot.uid !== uid) return null;
  if (Date.now() - _snapshot.ts > maxAgeMs) return null;
  return _snapshot.state;
}

/**
 * Write the current dashboard state to the module cache. Called from
 * useDashboardData() after a successful load.
 */
export function writeSnapshot(uid, state) {
  if (!uid || !state) return;
  _snapshot = { uid, state, ts: Date.now() };
}

/**
 * Clear the module cache. Called by invalidateBriefing() and by any
 * signal-mutating flow that needs the next dashboard mount to hit the
 * network fresh (e.g., after an import, a category remap, etc.).
 */
export function clearSnapshot() {
  _snapshot = null;
}
